/**
 * Intake configuration — types & constants (FHIR `PlanDefinition` storage model)
 *
 * Used by pilot seeding, PlanDefinition serialization, and client-portal language
 * variant discovery. Storage model:
 *   - The published intake config is a single `PlanDefinition` keyed by
 *     `INTAKE_CONFIG_CANONICAL_URL` with `status = active`. Drafts use the
 *     same canonical URL with `status = draft`.
 *   - Default intake is one outer `PlanDefinition.action` (kind `default`).
 *   - Each service type uses **one** `service-type` action — that action
 *     contains the list of forms/templates for the service type. Per-form
 *     age + language *variants* live on each child action via the
 *     {@link INTAKE_ITEM_VARIANT_EXTENSION} composite extension. The previous
 *     `service-type-age` actions are read for backward compatibility (each
 *     stored item is migrated into a variant on the matching everyone item,
 *     or appended as a standalone item with an age qualifier baked in).
 *   - Event rules (kind `event`) layer additional templates on top of the
 *     above whenever a downstream signal is recorded for the client (e.g.
 *     self-pay billing, safety planning, ESA request).
 *   - Each template row is a child `action` with `definitionCanonical` set
 *     to the `Questionnaire.url` (logical identity of the *base* template).
 *   - Per-item enable + ordering are captured via small custom extensions
 *     so every item is round-trippable.
 *
 * Removed in the variant redesign:
 *   - The page-level "Global age group rules" section (kind `age-group`).
 *     Existing PlanDefinitions may still contain those actions; they are
 *     dropped silently on load for forward compatibility.
 *
 * Branding:
 *   - All stored intake configs are tagged with a brand (`unify` for now).
 *     The brand defaults to `unify` when missing so legacy data continues
 *     to work without migration. Future per-organization branding can extend
 *     {@link IntakeBrand}.
 */
import type { Coding, PlanDefinition } from '@medplum/fhirtypes';

/** Canonical URL for the single Unify intake configuration `PlanDefinition`. */
export const INTAKE_CONFIG_CANONICAL_URL =
  'https://unify.health/fhir/PlanDefinition/intake-configuration';

/** Human title used as a secondary search affordance for the config. */
export const INTAKE_CONFIG_TITLE = 'Unify Intake Configuration';

/** Service type CodeSystem URL (PRD §1). */
export const SERVICE_TYPE_CODESYSTEM_URL =
  'https://unify.health/fhir/CodeSystem/service-types';

/**
 * Built-in service type seed used when the CodeSystem is not yet present in
 * the project. The PRD lists these codes — keep in sync.
 */
export const DEFAULT_SERVICE_TYPE_CONCEPTS: Coding[] = [
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'individual-therapy', display: 'Individual Therapy' },
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'child-therapy', display: 'Child Therapy' },
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'couples-therapy', display: 'Couples Therapy' },
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'group-therapy', display: 'Group Therapy' },
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'family-therapy', display: 'Family Therapy' },
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'iop', display: 'Intensive Outpatient Program' },
  { system: SERVICE_TYPE_CODESYSTEM_URL, code: 'medication-management', display: 'Medication Management' },
];

/**
 * Intake event CodeSystem URL — codes for downstream triggers that add forms
 * after the initial intake (PRD §3.1).
 */
export const INTAKE_EVENT_CODESYSTEM_URL =
  'https://unify.health/fhir/CodeSystem/intake-events';

/**
 * Built-in catalog of intake events. Codes are stable identifiers persisted in
 * `PlanDefinition.action` (kind=`event`); displays are admin-facing labels.
 */
export const DEFAULT_INTAKE_EVENT_CONCEPTS: Coding[] = [
  { system: INTAKE_EVENT_CODESYSTEM_URL, code: 'self-pay', display: 'Self pay' },
  {
    system: INTAKE_EVENT_CODESYSTEM_URL,
    code: 'esa-request',
    display: 'Emotional Support Animal request',
  },
  { system: INTAKE_EVENT_CODESYSTEM_URL, code: 'safety-planning', display: 'Safety planning needed' },
  {
    system: INTAKE_EVENT_CODESYSTEM_URL,
    code: 'care-coordination',
    display: 'Care coordination / release needed',
  },
  { system: INTAKE_EVENT_CODESYSTEM_URL, code: 'labs-requested', display: 'Labs requested' },
  {
    system: INTAKE_EVENT_CODESYSTEM_URL,
    code: 'controlled-substances',
    display: 'Controlled substances policy applies',
  },
  {
    system: INTAKE_EVENT_CODESYSTEM_URL,
    code: 'community-based-services',
    display: 'Community-based services',
  },
];

// ---------------------------------------------------------------------------
// Custom extensions used to round-trip editor metadata through PlanDefinition
// ---------------------------------------------------------------------------

/** Stable rule kind tag on outer PlanDefinition.action entries. */
export const INTAKE_RULE_KIND_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-rule-kind';

/** Service type code attached to outer PlanDefinition.action entries when kind='service-type'. */
export const INTAKE_RULE_SERVICE_TYPE_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-rule-service-type';

/**
 * Age group code (`minor` | `adult`) attached to outer actions when kind='age-group'.
 *
 * Still emitted on legacy `service-type-age` actions during a one-time migration
 * window; the new editor never writes these.
 */
export const INTAKE_RULE_AGE_GROUP_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-rule-age-group';

/** Event code (e.g. `self-pay`) attached to outer actions when kind='event'. */
export const INTAKE_RULE_EVENT_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-rule-event';

/** Per-item enabled flag (default true) on inner PlanDefinition.action entries. */
export const INTAKE_ITEM_ENABLED_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-item-enabled';

/**
 * Admin-facing template category on child actions. All rows reference FHIR
 * `Questionnaire`; this distinguishes forms vs assessments and similar for UX.
 */
export const INTAKE_ITEM_TEMPLATE_KIND_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-item-template-kind';

/**
 * Composite extension on a child intake item action that lists per-form
 * variants (age + language alternatives that share a logical form).
 *
 * Sub-extensions (one per variant entry inside `extension`):
 *   - `templateUrl` (string) — Questionnaire.url for the variant
 *   - `title` (string) — cached label shown in admin UI
 *   - `ageQualifier` (string?) — `minor` | `adult` | omitted for "all ages"
 *   - `languageCode` (string?) — BCP-47 code or omitted for "default language"
 *   - `enabled` (boolean?) — default true
 *
 * The base/default template is always the action's own `definitionCanonical`
 * (i.e. there is no separate `isDefault` flag stored — the default *is* the
 * row itself).
 */
export const INTAKE_ITEM_VARIANT_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-item-variant';

/**
 * Brand of the published intake configuration. Stored as an extension on the
 * `PlanDefinition` itself. Defaults to `unify` when absent.
 */
export const INTAKE_CONFIG_BRAND_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/intake-config-brand';

// ---------------------------------------------------------------------------
// Domain model
// ---------------------------------------------------------------------------

export type AgeGroup = 'minor' | 'adult';

export const AGE_GROUPS: AgeGroup[] = ['minor', 'adult'];

export const AGE_GROUP_LABELS: Record<AgeGroup, string> = {
  minor: 'Minor',
  adult: 'Adult',
};

/** Stable in-memory id for an editor item (not persisted). */
export type IntakeItemEditorId = string;

/** Logical template category for labeling (all rows use `Questionnaire` resources). */
export type IntakeTemplateKind = 'form' | 'assessment' | 'consent' | 'policy' | 'other';

export const INTAKE_TEMPLATE_KINDS: IntakeTemplateKind[] = [
  'form',
  'assessment',
  'consent',
  'policy',
  'other',
];

export const INTAKE_TEMPLATE_KIND_LABELS: Record<IntakeTemplateKind, string> = {
  form: 'Form',
  assessment: 'Assessment',
  consent: 'Consent',
  policy: 'Policy',
  other: 'Other',
};

export const DEFAULT_INTAKE_TEMPLATE_KIND: IntakeTemplateKind = 'form';

export function isIntakeTemplateKind(value: string | undefined): value is IntakeTemplateKind {
  return (
    value === 'form' ||
    value === 'assessment' ||
    value === 'consent' ||
    value === 'policy' ||
    value === 'other'
  );
}

/**
 * Brand the intake configuration belongs to. Today only `unify` is supported;
 * the type is open-ended so per-organization branding can be added later
 * without changing the storage shape.
 */
/** Primary deployment value is `unify`; other strings reserved for future branding. */
export type IntakeBrand = string;

export const DEFAULT_INTAKE_BRAND: IntakeBrand = 'unify';

export const INTAKE_BRAND_LABELS: Record<string, string> = {
  unify: 'Unify',
};

/**
 * One *variant* of a logical form: typically a translation, an age-specific
 * version, or both. The "default" / "base" template is not stored here — it
 * is the parent {@link IntakeTemplateItem.templateUrl} itself.
 *
 * Uniqueness rule: within a single `IntakeTemplateItem`, the tuple
 * `(ageQualifier, languageCode)` must be unique across both the base
 * template and all variants. This is enforced at validation time and
 * surfaced in the variant editor modal as duplicate-prevention UI.
 */
export interface IntakeTemplateVariant {
  /** Stable editor-only id (not persisted). */
  id: IntakeItemEditorId;
  /** Logical canonical URL of the variant Questionnaire template. */
  templateUrl: string;
  /** Cached display label (Questionnaire.title cache). */
  title: string;
  /**
   * Age cohort this variant applies to. `undefined` / `null` means the
   * variant applies to all ages (typical for pure language translations).
   */
  ageQualifier?: AgeGroup | null;
  /**
   * BCP-47 language code (`es`, `zh-Hans`, …). `undefined` / `null` means
   * the variant uses the default language (English in our deployment).
   */
  languageCode?: string | null;
  /** Whether this variant is enabled. Defaults to true when missing. */
  enabled: boolean;
}

/**
 * One template reference inside a rule (default / service-type / event).
 *
 * `templateUrl` is the logical canonical identity (`Questionnaire.url`); the
 * latest published version is resolved at assignment time. Variants attach
 * age and/or language alternatives to the same logical row.
 */
export interface IntakeTemplateItem {
  /** Stable editor-only id (not persisted). */
  id: IntakeItemEditorId;
  /** Logical canonical URL of the *base* Questionnaire template. */
  templateUrl: string;
  /** Cached display label shown when admins read the config. */
  title: string;
  /** Whether this item is enabled (disabled items are kept for history but not assigned). */
  enabled: boolean;
  /**
   * What kind of template this row represents (forms, assessments, etc.).
   * Omitted / unknown in stored data defaults to {@link DEFAULT_INTAKE_TEMPLATE_KIND}.
   */
  templateKind?: IntakeTemplateKind;
  /**
   * Per-form variants (age and/or language alternatives). Empty / missing
   * means the base template is used unconditionally — preserves legacy
   * single-template behavior.
   */
  variants?: IntakeTemplateVariant[];
  /**
   * Optional age qualifier on the *base* template itself. When set, the row
   * is treated as age-restricted (e.g. used for legacy minor/adult-only
   * templates that didn't have an "all ages" base). Pure additions to the
   * variants list never touch this field.
   */
  baseAgeQualifier?: AgeGroup | null;
}

/** Section: default intake items. */
export interface DefaultIntakeRule {
  items: IntakeTemplateItem[];
}

/**
 * Per–service-type rule: a single ordered list of forms/templates. Age and
 * language variants live on each item via {@link IntakeTemplateItem.variants}
 * — the page no longer carries a top-level minor/adult split.
 */
export interface ServiceTypeRule {
  /** Stable id for the editor — derived from `serviceTypeCode` once chosen. */
  id: string;
  /** CodeSystem code (e.g. `child-therapy`). */
  serviceTypeCode: string;
  /** Templates for every client with this service type. */
  items: IntakeTemplateItem[];
}

/**
 * Event-triggered rule: extra templates that fire when an event code is
 * recorded for the client (post-intake or alongside it). Multiple events can
 * fire for the same client; each rule contributes its items independently.
 */
export interface IntakeEventRule {
  /** Stable id for the editor — derived from `eventCode` once chosen. */
  id: string;
  /** CodeSystem code from `INTAKE_EVENT_CODESYSTEM_URL` (e.g. `self-pay`). */
  eventCode: string;
  /** Templates assigned when the event fires for a client. */
  items: IntakeTemplateItem[];
}

/** The full editable configuration. */
export interface IntakeConfigurationDraft {
  /** Source PlanDefinition id, when editing a saved draft. */
  id?: string;
  /** Version string (PlanDefinition.version) of the source resource, if any. */
  version?: string;
  /** Brand the configuration applies to. Defaults to `unify`. */
  brand: IntakeBrand;
  defaults: DefaultIntakeRule;
  serviceTypeRules: ServiceTypeRule[];
  /** Optional rules triggered by downstream client events; layered on top of the others. */
  eventRules: IntakeEventRule[];
}

/** Loaded `PlanDefinition` bundle for intake config (e.g. mock fixtures). */
export interface IntakeConfigurationData {
  active: PlanDefinition | undefined;
  draft: PlanDefinition | undefined;
  history: PlanDefinition[];
}

// ---------------------------------------------------------------------------
// Assignment expansion model (used by validation / runtime assignment helpers)
// ---------------------------------------------------------------------------

/** Which rule contributed a row when expanding intake for a client context. */
export type IntakePreviewSource =
  | 'default'
  | 'service-type'
  | 'event';

/** One resolved row from rule expansion (e.g. validation path checks). */
export interface IntakePreviewItem {
  /** The Questionnaire.url that was actually selected (after variant resolution). */
  templateUrl: string;
  title: string;
  /** Resolved exact version (Questionnaire.version), if a published version exists. */
  resolvedVersion?: string;
  /** True when no active/published Questionnaire matches templateUrl. */
  unresolved: boolean;
  /** Source of the row in the rule expansion. */
  source: IntakePreviewSource;
  /** When the resolved row came from a variant rather than the base template. */
  variantApplied?: {
    ageQualifier?: AgeGroup | null;
    languageCode?: string | null;
  };
}

/** Validation issue shape for intake configuration checks (if reintroduced). */
export interface IntakeValidationIssue {
  severity: 'error' | 'warning';
  message: string;
  /** Optional anchor for inline rendering. */
  ruleKind?: 'default' | 'service-type' | 'event';
  ruleKey?: string;
}
